import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Stack, Group, Paper, Text, TextInput, Select, SegmentedControl, Button, Tooltip, Anchor,
  Pagination, Alert, Badge, Skeleton as MSkeleton,
} from '@mantine/core'
import {
  IconSearch, IconAlertTriangle, IconAlertCircle, IconCircleCheck, IconExternalLink, IconInfoCircle,
  IconLock, IconPlayerPlay,
} from '@tabler/icons-react'
import {
  PageTitle, SectionCard, StatusBadge, SeverityBadge, EmptyState, DataTable, FilterChip, MetaList,
} from '@lighthouse/design'
import type { DataTableColumn } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { FindingDetailDrawer } from '../components/data-comb/FindingDetailDrawer'
import {
  RUN_NOW_PATH, SEVERITY_LABEL, SEVERITY_TONE, acknowledgeGate, ageLabel, combDuration, emptyVerdict, filterOptions,
  findingsView, passErrorView, passOptions, passValue, provenanceLabel, resolvePass, runNowGate, runStatusLabel,
  runStatusTone, sameRunId, summaryRefreshFailed, summaryView,
} from '../components/data-comb/combFormat'
import type { EmptyVerdict, ResolvedPass, RunNowGate } from '../components/data-comb/combFormat'
import { useCombFinding, useCombFindings, useCombRuns, useCombSummary } from '../hooks/useDataComb'
import { useEtlJobCatalog } from '../hooks/useEtlJobs'
import { isEntraConfigured } from '../auth'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, errorKind, errorSummary, exactNumber, relativeTime } from '../utils/format'
import { hangfireJobUrl } from '../utils/hangfireUrl'
import { RULE_LABELS } from '../types/dataComb'
import type { CombFinding, CombRun, CombSeverity, CombSummary, FindingState } from '../types/dataComb'

/**
 * Data Comb — what the nightly sweep found.
 *
 * The comb has been writing findings to `Etl.DataCombFinding` since it shipped and
 * nothing rendered them, which is the same as not having them. This is that
 * surface.
 *
 * Four decisions shape the page:
 *
 *  1. **It shows one pass, not all history.** Every run re-inserts a row per
 *     (rule, schema, table, column, provenance), so an unscoped list would show
 *     the same problem once per night. The question an operator has is "what is
 *     wrong now" — the latest completed pass — and `firstSeenAt` travels with
 *     each finding so a standing condition still reads as one. An earlier pass is
 *     reachable through the Pass select and `?runId=`, and then the page says
 *     less: every count the summary carries is the latest pass's.
 *
 *     Provenance is in that key because the profiler measures per source system:
 *     one column can be empty for Oracle-loaded rows and populated for
 *     hand-entered ones, and those are two findings, not one.
 *  2. **Findings are not failures.** A pass that finds things succeeded at its
 *     job; only a sweep that could not run failed. The status wording follows
 *     that, and the run header leads with coverage rather than with alarm.
 *  3. **Nothing is inferred client-side.** Counts, "new since the previous pass"
 *     and the acknowledge permission all arrive from the server. The Admin policy
 *     degrades to "any authenticated user" in Development, so a client deriving
 *     that last one from role claims would disagree with the endpoint exactly
 *     where it matters. Since R3 (2026-10-01) acknowledging and restoring are
 *     pinned to an Entra sign-in, so the drawer is gated as the Scheduler gates
 *     its writes: on the job catalogue's `canAcknowledgeCombFindings`
 *     (combFormat.ts `acknowledgeGate`, failing closed), not on the summary's
 *     `permission`, which the server still serves for an older bundle.
 *  4. **A failed or unfinished request is never an answer.** Loading, a failed
 *     call, a comb that never ran, a pass that failed and a clean pass all read
 *     differently. The decisions live in combFormat.ts (summaryView,
 *     findingsView, emptyVerdict) so each is tested.
 */

const PAGE_SIZE = 50

/** The Pass select's list. */
const PASS_LIST = 20

/** How far back a deep-linked pass that is not in the short list is looked for (the endpoint's cap). */
const PASS_LOOKUP = 100

export function DataCombPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedRunId = searchParams.get('runId')
  const requestedFindingId = searchParams.get('finding')?.trim() || null

  const { data: summary, isError: summaryIsError, error: summaryErr, dataUpdatedAt: summaryUpdatedAt } = useCombSummary()
  const view = summaryView(summary, summaryIsError)
  const ready = view === 'ready' && summary !== undefined
  // A held summary whose refresh failed: still shown, and said to be the one last loaded.
  const refreshFailed = summaryRefreshFailed(summary, summaryIsError)
    ? <SummaryRefreshFailed error={summaryErr} updatedAt={summaryUpdatedAt} />
    : null

  const recent = useCombRuns(PASS_LIST)
  // A deep link older than the short list: look further back once, and only when
  // the short list was full (a shorter one already holds every pass there is).
  const shortPass = ready ? resolvePass(requestedRunId, summary, recent.data) : null
  const needLookup = shortPass?.kind === 'unlisted' && recent.isSuccess && recent.data.length >= PASS_LIST
  const lookup = useCombRuns(PASS_LOOKUP, needLookup)
  const pass = ready ? resolvePass(requestedRunId, summary, recent.data, lookup.data) : null

  const { data: catalog } = useEtlJobCatalog()
  const runNow = runNowGate(catalog)
  const openScheduler = () => navigate(RUN_NOW_PATH)
  // Whether this build can sign anyone in with Entra decides the refusal's remedy; both writes are bearer-only.
  const entraConfigured = isEntraConfigured()
  const ackGate = acknowledgeGate(catalog, entraConfigured)

  const [severity, setSeverity] = useState<CombSeverity | null>(null)
  const [ruleId, setRuleId] = useState<string | null>(null)
  const [sourceSystem, setSourceSystem] = useState<string | null>(null)
  const [state, setState] = useState<FindingState>('outstanding')
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selected, setSelected] = useState<CombFinding | null>(null)

  // The page number belongs to one pass: moving to another (the select, or back and
  // forward through ?runId=) starts at page 1 rather than at a page it may not have.
  const passKey = pass?.queryRunId ?? null
  const [paging, setPaging] = useState<{ pass: string | null; page: number }>({ pass: null, page: 1 })
  const page = paging.pass === passKey ? paging.page : 1
  const setPage = (next: number) => setPaging({ pass: passKey, page: next })

  // Not memoized: React Query hashes the key structurally, so a fresh object with
  // the same fields is the same query.
  const query = {
    runId: passKey ?? undefined,
    severity: severity ?? undefined,
    ruleId: ruleId ?? undefined,
    sourceSystem: sourceSystem ?? undefined,
    state,
    search: appliedSearch || undefined,
    skip: (page - 1) * PAGE_SIZE,
    top: PAGE_SIZE,
  }

  // Nothing is asked for a malformed id, and nothing exists to ask for on a pass
  // with no completion (findings are saved together with it).
  const findingsEnabled = pass !== null && pass.queryRunId !== null && !pass.isIncomplete
  const findingsQuery = useCombFindings(query, findingsEnabled)
  const linked = useCombFinding(requestedFindingId)

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setPage(1)
    setter(value)
  }

  const selectPass = (value: string | null) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      if (!value || sameRunId(value, summary?.latestRun?.runId)) next.delete('runId')
      else next.set('runId', value)
      return next
    })
  }

  const closeDrawer = () => {
    setSelected(null)
    if (requestedFindingId) {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          next.delete('finding')
          return next
        },
        { replace: true },
      )
    }
  }

  const columns: DataTableColumn<CombFinding>[] = [
    {
      key: 'severity',
      header: 'Severity',
      width: 130,
      render: (f) => (
        <SeverityBadge severity={SEVERITY_TONE[f.severity]} label={SEVERITY_LABEL[f.severity]} />
      ),
    },
    {
      key: 'target',
      header: 'Target',
      render: (f) => (
        <Text size="sm" ff="monospace">
          {f.target}
        </Text>
      ),
    },
    {
      // Not decoration, and not optional: the profiler measures per provenance, so the
      // same column legitimately produces one finding per source system. Without this
      // column those arrive as rows a reader cannot tell apart and would reasonably
      // report as a duplication bug.
      key: 'provenance',
      header: 'Provenance',
      width: 130,
      render: (f) => (
        <Text size="sm" c={f.sourceSystem ? undefined : 'dimmed'}>
          {provenanceLabel(f.sourceSystem)}
        </Text>
      ),
    },
    {
      key: 'rule',
      header: 'Rule',
      width: 180,
      render: (f) => <Text size="sm">{RULE_LABELS[f.ruleId] ?? f.ruleId}</Text>,
    },
    {
      key: 'observed',
      header: 'Observed',
      width: 170,
      render: (f) => (
        <Text size="sm" c="dimmed">
          {f.observed ?? '—'}
        </Text>
      ),
    },
    {
      key: 'age',
      header: 'Standing for',
      width: 120,
      render: (f) =>
        f.isNew ? (
          <Badge size="sm" variant="light">New</Badge>
        ) : (
          <Text size="sm" c="dimmed">{ageLabel(f.ageDays, false)}</Text>
        ),
    },
    {
      key: 'acknowledged',
      header: 'State',
      width: 130,
      render: (f) =>
        f.acknowledgedAt ? (
          <Text size="sm" c="dimmed">
            Acknowledged
          </Text>
        ) : (
          <Text size="sm">Outstanding</Text>
        ),
    },
  ]

  const title = (
    <PageTitle subtitle="What the nightly sweep found in the warehouse, and how long it has been true">
      Data Comb
    </PageTitle>
  )

  if (view === 'loading') {
    return (
      <Stack gap="md">
        <MSkeleton height={120} />
        <MSkeleton height={400} />
      </Stack>
    )
  }

  if (view === 'error') {
    return (
      <Stack gap="lg" pb="xl">
        {title}
        <SummaryError error={summaryErr} />
      </Stack>
    )
  }

  if (!ready || !pass) {
    return (
      <Stack gap="lg" pb="xl">
        {title}
        {refreshFailed}
        <NeverRun runNow={runNow} onRunNow={openScheduler} />
      </Stack>
    )
  }

  const isLatest = pass.kind === 'latest'
  const options = filterOptions(summary, isLatest, { ruleId, sourceSystem })
  const findings = findingsQuery.data
  const fView = findingsView(findings, findingsQuery.isLoading, findingsQuery.isError)
  const filtered = Boolean(severity || ruleId || sourceSystem || appliedSearch)
  const passListPending = recent.isLoading || lookup.isLoading

  return (
    <Stack gap="lg" pb="xl">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        {title}
        <Select
          label="Pass"
          w={380}
          allowDeselect={false}
          value={passValue(pass)}
          onChange={selectPass}
          data={passOptions(recent.data, summary.latestRun, pass)}
          description={
            recent.isError && !recent.data
              ? 'The list of passes could not be loaded, so only the latest is offered.'
              : undefined
          }
        />
      </Group>

      {refreshFailed}

      {pass.kind === 'malformed' ? (
        <MalformedPass requested={requestedRunId ?? ''} onShowLatest={() => selectPass(null)} />
      ) : pass.run === null ? (
        <UnlistedPassHeader
          runId={pass.queryRunId ?? ''}
          pending={passListPending}
          listFailed={recent.isError && !recent.data}
          searched={(lookup.data ?? recent.data)?.length ?? 0}
          runNow={runNow}
          onRunNow={openScheduler}
        />
      ) : (
        <RunHeader run={pass.run} pass={pass} summary={summary} runNow={runNow} onRunNow={openScheduler} />
      )}

      {pass.kind !== 'malformed' && pass.isIncomplete && pass.run && <IncompletePass run={pass.run} />}

      {pass.kind !== 'malformed' && !pass.isIncomplete && (
        <>
          <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <Stack gap="sm">
              <Group gap="xs">
                <FilterChip active={severity === null} onClick={() => resetPage(setSeverity)(null)}>
                  All severities
                </FilterChip>
                {options.severityChips.map((chip) => (
                  <FilterChip
                    key={chip.severity}
                    active={severity === chip.severity}
                    onClick={() => resetPage(setSeverity)(severity === chip.severity ? null : chip.severity)}
                  >
                    {chip.label}
                  </FilterChip>
                ))}
                {options.countsCaption && (
                  <Text size="xs" c="dimmed">
                    {options.countsCaption}
                  </Text>
                )}
              </Group>

              <Group align="flex-end" gap="sm">
                <Select
                  label="Rule"
                  placeholder="Every rule"
                  clearable
                  w={260}
                  value={ruleId}
                  onChange={resetPage(setRuleId)}
                  data={options.rules}
                />
                <Select
                  label="Provenance"
                  placeholder="Every provenance"
                  clearable
                  w={200}
                  value={sourceSystem}
                  onChange={resetPage(setSourceSystem)}
                  data={options.sources}
                />
                <TextInput
                  label="Search"
                  placeholder="Schema, table, column or message"
                  leftSection={<IconSearch size={16} />}
                  w={320}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') resetPage(setAppliedSearch)(search)
                  }}
                />
                <Button variant="default" onClick={() => resetPage(setAppliedSearch)(search)}>
                  Search
                </Button>
                <SegmentedControl
                  value={state}
                  onChange={(v) => resetPage(setState)(v as FindingState)}
                  data={options.states}
                />
              </Group>
            </Stack>
          </Paper>

          <SectionCard
            title="Findings"
            subtitle={
              findings && fView !== 'error'
                ? `${exactNumber(findings.totalCount)} matching this filter, from ${pass.run?.runKey ?? pass.queryRunId}`
                : undefined
            }
          >
            {fView === 'error' ? (
              <FindingsError error={findingsQuery.error} />
            ) : (
              <DataTable
                columns={columns}
                // Rows only from a page that arrived; until then the table is loading,
                // never an empty result.
                rows={fView === 'loading' ? [] : (findings?.data ?? [])}
                rowKey={(f) => f.id}
                loading={fView === 'loading'}
                onRowClick={setSelected}
                empty={
                  <NothingMatched
                    verdict={emptyVerdict(state, filtered, pass, summary)}
                    runKey={pass.run?.runKey ?? pass.queryRunId ?? ''}
                  />
                }
              />
            )}

            {fView === 'rows' && findings && findings.totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination value={page} onChange={setPage} total={findings.totalPages} />
              </Group>
            )}
          </SectionCard>
        </>
      )}

      <FindingDetailDrawer
        finding={selected ?? linked.data ?? null}
        gate={ackGate}
        entraConfigured={entraConfigured}
        opened={selected !== null || requestedFindingId !== null}
        onClose={closeDrawer}
        requestedId={selected ? null : requestedFindingId}
        linkError={linked.error}
        latestRunId={summary.latestRun?.runId ?? null}
      />
    </Stack>
  )
}

/**
 * "Run a pass now": to the Scheduler, opened on the comb's job — gated as the
 * Scheduler gates Run (runNowGate).
 *
 * `data-disabled` rather than `disabled`: a natively disabled button fires no
 * pointer events, so the tooltip carrying the server's reason would never show,
 * and a control that is off without saying why teaches nothing.
 */
function RunNowButton({ gate, onClick }: { gate: RunNowGate; onClick: () => void }) {
  return (
    <Tooltip label={gate.tooltip} disabled={!gate.tooltip} multiline w={300} withArrow>
      <Button
        size="xs"
        variant="default"
        leftSection={gate.disabled ? <IconLock size={14} /> : <IconPlayerPlay size={14} />}
        data-disabled={gate.disabled || undefined}
        aria-disabled={gate.disabled || undefined}
        onClick={(e) => {
          if (gate.disabled) {
            e.preventDefault()
            return
          }
          onClick()
        }}
      >
        Run a pass now
      </Button>
    </Tooltip>
  )
}

/**
 * The comb has never completed a pass here.
 *
 * Deliberately not the same as "no findings". A clean warehouse and an unchecked
 * one look identical in an empty table, and reporting the second as the first is
 * the exact failure this whole surface exists to avoid. Only a LOADED summary
 * with no latest pass reaches this (summaryView).
 */
function NeverRun({ runNow, onRunNow }: { runNow: RunNowGate; onRunNow: () => void }) {
  return (
    <EmptyState
      title="The Data Comb has not completed a pass against this database"
      message={
        'Nothing here has been checked yet — which is not the same as nothing being wrong. The comb runs '
        + 'nightly at 04:15, and can be started on demand from the Scheduler.'
      }
      action={<RunNowButton gate={runNow} onClick={onRunNow} />}
    />
  )
}

/**
 * A refresh of a summary already on screen failed. The summary stays (it is the last thing the server
 * said), but under a notice that it is not current — as the Explorer's RefreshFailed does.
 */
function SummaryRefreshFailed({ error, updatedAt }: { error: unknown; updatedAt: number }) {
  const said = errorSummary(error)
  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconAlertTriangle size={16} />}
      title="Could not refresh — showing the summary as last loaded"
      data-state="summary-refresh-failed"
    >
      <Text size="sm">
        {updatedAt > 0 ? <>Loaded {relativeTime(new Date(updatedAt).toISOString())}. </> : null}
        What follows may not be the latest pass.
        {said && <> The server said: {said}</>}
      </Text>
    </Alert>
  )
}

/** A pass's status. A tone this build does not know is a plain gray badge — never the green of a clean pass. */
function PassStatusBadge({ tone, label }: { tone: ReturnType<typeof runStatusTone>; label: string }) {
  return tone
    ? <StatusBadge status={tone} label={label} />
    : <Badge color="gray" variant="light" radius="sm">{label}</Badge>
}

/** The summary request failed, which says nothing about the warehouse. */
function SummaryError({ error }: { error: unknown }) {
  if (errorKind(error) === 'forbidden') {
    return <RestrictedState surface="The Data Comb" reason={errorSummary(error)} />
  }
  const said = errorSummary(error)
  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not load the Data Comb"
      message={
        <>
          The request failed, which says nothing about whether the warehouse is clean or whether the comb
          has run. A rejected sign-in and an API that is not running land here identically.
          {said && <> The server said: {said}</>}
        </>
      }
    />
  )
}

/** The findings request failed — worded so it can never be read as an empty, clean list. */
function FindingsError({ error }: { error: unknown }) {
  if (errorKind(error) === 'forbidden') {
    return <RestrictedState surface="Data Comb findings" reason={errorSummary(error)} />
  }
  const said = errorSummary(error)
  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not load the findings"
      message={
        <>
          The request failed, which says nothing about whether the warehouse is clean. The pass above is
          still what the server reported; only its findings did not come back.
          {said && <> The server said: {said}</>}
        </>
      }
    />
  )
}

/** What an empty list may say — decided by emptyVerdict, never by the empty array alone. */
function NothingMatched({ verdict, runKey }: { verdict: EmptyVerdict; runKey: string }) {
  switch (verdict.kind) {
    case 'clean':
      return (
        <EmptyState
          icon={<IconCircleCheck size={28} stroke={1.5} />}
          title="Nothing outstanding"
          message={
            verdict.acknowledged > 0
              ? `The last pass raised nothing new. ${verdict.acknowledged} acknowledged finding${
                  verdict.acknowledged === 1 ? ' is' : 's are'
                } still being carried forward.`
              : 'The last pass measured the warehouse and found nothing to report.'
          }
        />
      )
    case 'failed-pass':
      return (
        <EmptyState
          icon={<IconAlertTriangle size={28} stroke={1.5} />}
          title="This pass failed, so an empty list is not a clean result"
          message={
            'The sweep stopped before it finished. An empty list here reflects what it did not get to '
            + 'measure, not something it checked and found clean.'
          }
        />
      )
    case 'unknown-pass':
      return (
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="Nothing is listed for this pass"
          message={
            'This page could not describe the pass, so it cannot say whether the pass completed — an empty '
            + 'list here is not a clean result.'
          }
        />
      )
    case 'earlier-none':
      return (
        <EmptyState
          icon={<IconInfoCircle size={28} stroke={1.5} />}
          title="Nothing outstanding on this pass"
          message={
            `${runKey} recorded no finding that is still unacknowledged. Whether the warehouse is clean now `
            + 'is the latest pass\'s question, not this one\'s.'
          }
        />
      )
    default:
      return <EmptyState title="No findings match this filter" />
  }
}

/** `?runId=` that is not a run id. Nothing was asked for. */
function MalformedPass({ requested, onShowLatest }: { requested: string; onShowLatest: () => void }) {
  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title={`“${requested}” is not a pass id`}
      message="A pass link carries the pass's run id, a GUID. This one does not, so nothing was asked for."
      action={
        <Button variant="default" onClick={onShowLatest}>
          Show the latest pass
        </Button>
      }
    />
  )
}

/**
 * A well-formed pass id this page has not listed. Its findings are still asked
 * for by id; its header is what cannot be shown.
 */
function UnlistedPassHeader({
  runId, pending, listFailed, searched, runNow, onRunNow,
}: {
  runId: string
  pending: boolean
  listFailed: boolean
  searched: number
  runNow: RunNowGate
  onRunNow: () => void
}) {
  return (
    <SectionCard title="Pass" action={<RunNowButton gate={runNow} onClick={onRunNow} />}>
      {pending ? (
        <MSkeleton height={80} />
      ) : (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title={`Pass ${runId} is not listed`}>
          <Text size="sm">
            {listFailed
              ? 'The list of passes could not be loaded, so this page cannot describe this pass.'
              : `No pass with this id is among the ${searched} most recent. It may be older, trimmed by `
                + 'retention, or the link may be wrong.'}{' '}
            Its findings are still asked for by id below, but without its header this page cannot say
            whether it completed, so an empty list is not a clean result.
          </Text>
        </Alert>
      )}
    </SectionCard>
  )
}

/** A pass with no completion: its findings do not exist yet, so there is no list to show. */
function IncompletePass({ run }: { run: CombRun }) {
  return (
    <SectionCard title="Findings">
      <EmptyState
        icon={<IconInfoCircle size={28} stroke={1.5} />}
        title="This pass has not completed"
        message={
          `${run.runKey} started ${relativeTime(run.startedAt)} and has no completion recorded. The comb `
          + 'saves a pass\'s findings together with its completion, so there are none to list, and an empty '
          + 'list here would not be a result. A pass still not completed long after it started most likely '
          + 'stopped with its worker.'
        }
      />
    </SectionCard>
  )
}

/** A Hangfire job id: a link into the dashboard when this build knows where it is, plain text when not. */
function HangfireJobRef({ id }: { id: string }) {
  const url = hangfireJobUrl(id)
  if (!url) {
    return (
      <Text span size="sm" ff="monospace">
        {id}
      </Text>
    )
  }
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" size="sm" ff="monospace">
      {id} <IconExternalLink size={12} />
    </Anchor>
  )
}

/**
 * What the pass on screen did — the latest, or the one chosen by run id.
 *
 * Coverage first — tables and columns measured — because a findings count is only
 * meaningful next to how much was looked at. A pass that skipped half the
 * warehouse and found nothing is not good news, and `tablesSkipped` is called out
 * rather than left for someone to notice. A pass with no completion has measured
 * nothing yet, so its coverage and counts are not measured rather than zero.
 */
function RunHeader({
  run, pass, summary, runNow, onRunNow,
}: {
  run: CombRun
  pass: ResolvedPass
  summary: CombSummary
  runNow: RunNowGate
  onRunNow: () => void
}) {
  const isLatest = pass.kind === 'latest'
  const incomplete = run.completedAt === null
  const passError = passErrorView(run)

  return (
    <SectionCard
      title={isLatest ? 'Latest pass' : 'Earlier pass'}
      subtitle={isLatest ? undefined : 'An earlier pass, chosen by run id. The Pass select above returns to the latest.'}
      action={
        <Group gap="xs">
          <PassStatusBadge
            tone={runStatusTone(run.status)}
            label={incomplete ? 'Not completed' : runStatusLabel(run.status)}
          />
          {isLatest && summary.newSincePreviousRun > 0 && (
            <Badge variant="light" color="orange">
              {summary.newSincePreviousRun} new since the previous pass
            </Badge>
          )}
          <RunNowButton gate={runNow} onClick={onRunNow} />
        </Group>
      }
    >
      <Stack gap="md">
        {passError && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title="The sweep did not complete">
            {passError.kind === 'withheld' ? (
              <Text size="sm" c="dimmed" data-state="detail-withheld">{DETAIL_ADMIN_ONLY}</Text>
            ) : (
              <Text size="sm">{passError.text}</Text>
            )}
            <Text size="sm" mt="xs">
              The findings below are from what it managed to measure before it stopped, so treat their
              coverage as partial.
            </Text>
          </Alert>
        )}

        {!incomplete && run.tablesSkipped > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              {run.tablesSkipped} table{run.tablesSkipped === 1 ? ' was' : 's were'} not measured on this
              pass. Each one is listed below as a <em>Not profiled</em> finding — a pass that quietly
              covered less than it claims is the failure mode this reporting exists to prevent.
            </Text>
          </Alert>
        )}

        <MetaList
          items={[
            { label: 'Run', value: run.runKey, mono: true },
            {
              label: 'Started',
              value: `${new Date(run.startedAt).toLocaleString()} (${relativeTime(run.startedAt)})`,
            },
            { label: 'Duration', value: combDuration(run.durationMs) },
            {
              label: 'Coverage',
              value: incomplete
                ? NOT_MEASURED
                : `${exactNumber(run.columnsProfiled)} columns across ${exactNumber(run.tablesProfiled)} tables`,
            },
            {
              label: 'Raised',
              value: incomplete
                ? NOT_MEASURED
                : `${run.findingsCritical} critical · ${run.findingsWarning} warning · ${run.findingsInfo} info`,
            },
            { label: 'Worker', value: run.workerName ?? NOT_MEASURED },
            // Only when recorded: passes before the worker captured its Hangfire id carry none.
            ...(run.hangfireJobId
              ? [{ label: 'Hangfire job', value: <HangfireJobRef id={run.hangfireJobId} /> }]
              : []),
          ]}
        />
      </Stack>
    </SectionCard>
  )
}
